import { useEffect, useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Rich } from "@/components/Rich";

/**
 * Accessible FAQ accordion + auto-generated FAQPage JSON-LD.
 *
 * Contract:
 *  - `items` is [{ question, answer }] where `answer` can be a plain string
 *    OR a JSX node. When it is a JSX node, pass a matching `answerText`
 *    (plain text, no HTML) alongside so the JSON-LD can carry a clean value.
 *  - Answers are ALWAYS present in the DOM (visually hidden via CSS max-h /
 *    aria-hidden) — never conditionally rendered — so crawlers see them.
 *  - The <script type="application/ld+json"> is generated from the SAME
 *    `items` array: a single source of truth, no drift between visible text
 *    and structured data.
 *  - Buttons expose `aria-expanded` and `aria-controls`, one <h3> per Q.
 */
export function FAQ({ items = [], testid = "faq", schemaId = "ld-faq" }) {
  const [openIndex, setOpenIndex] = useState(-1);
  const baseId = useId();

  // Inject the FAQPage JSON-LD in <head> so it is picked up by crawlers.
  useEffect(() => {
    if (!items.length) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: (typeof it.answer === "string" ? it.answer : it.answerText || "").trim(),
        },
      })),
    };
    const prev = document.getElementById(schemaId);
    if (prev) prev.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = schemaId;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [items, schemaId]);

  if (!items.length) return null;

  return (
    <div data-testid={testid} className="divide-y divide-[#D9D3C5] border-y border-[#D9D3C5]">
      {items.map((it, i) => {
        const isOpen = openIndex === i;
        const btnId = `${baseId}-btn-${i}`;
        const panelId = `${baseId}-panel-${i}`;
        return (
          <div key={i} className="py-2">
            <h3 className="m-0">
              <button
                id={btnId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? -1 : i)}
                data-testid={`${testid}-question-${i}`}
                className="w-full flex items-center justify-between gap-4 py-4 text-left text-[#203240] hover:text-[#B8956A] transition-colors"
              >
                <span className="font-display text-xl">{it.question}</span>
                <ChevronDown
                  size={20}
                  className={`shrink-0 text-[#B8956A] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
            </h3>
            {/* Answer is always in the DOM — never conditionally rendered. */}
            <div
              id={panelId}
              role="region"
              aria-labelledby={btnId}
              aria-hidden={!isOpen}
              data-testid={`${testid}-answer-${i}`}
              className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
            >
              <div className="overflow-hidden">
                <div className="pb-5 pr-8 text-[#697585] leading-relaxed">
                  {typeof it.answer === "string" ? <Rich value={it.answer} /> : it.answer}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
